import posed from 'react-pose';
import React from 'react';
import glam from 'glamorous';
import tween from 'popmotion/animations/tween';
import { Categories, Category } from 'module/Model/Categories';
import { createPureComponent } from 'module/Common';
import { PostListItem } from 'module/Model/PostList';
import { RouteType } from 'module/Model/Routes';
import { SizeForRatio } from 'module/Model/Size';
import { Wish } from 'module/Model/Wish';
import {
  NavProvider,
  LocalizedText,
  HoverProvider,
  WishHandler,
  Wave,
  Colors,
  MzImageDisplay,
} from 'module/View/Common';
import {
  ItemCardWrapper,
  LinkBlock,
  Overlay,
  TextOverlay,
  TextOverlayTitle,
  TextOverlayCategory,
  ImageSizeWrapper,
  DescriptionUnder,
  DescriptionUnderTitle,
  TextOverlayClient,
  AnnimColorCache,
  AnnimColorCacheProps,
  WaveWrapper,
  CategoriesWrapper,
} from './ItemCard.s';

const transition = ({ from, to, realHeight }: any) => tween({ from, to, duration: 300 });

const AnnimColorPose: React.SFC<
  AnnimColorCacheProps & { pose: string; onPoseComplete?: any; realHeight?: number }
> = (posed as any)(AnnimColorCache)({
  init: {
    y: '-100%',
    transition,
  },
  loading: {
    y: '0%',
    transition,
  },
  done: {
    y: '100%',
    transition,
  },
  requested: {
    y: '100%',
    transition,
  },
  'not-requested': {
    y: '100%',
    transition,
  },
});

type AnnimColorRequestedProps = {
  hide: boolean;
};

export const AnnimColorRequested = glam.div<AnnimColorRequestedProps>(p => ({
  position: 'absolute',
  left: -2,
  right: -2,
  top: -2,
  bottom: -2,
  background: 'white',
  zIndex: 5,
  ...(p.hide
    ? {
        transform: 'translate(0%, 102%)',
      }
    : {
        transitionDuration: '0.3s',
      }),
}));

const CategoryHandler = WishHandler.create<Categories>();

type Props = {
  item: PostListItem;
  cardSize: SizeForRatio;
  imageHeight?: number;
  descriptionUnderRef?: (elem: HTMLElement | null) => void;
  categoriesWish: Wish<Categories>;
  annimState: string;
  onImageLoaded?: () => void;
  onLoadingDone?: () => void;
  isTouch: boolean;
};

export const ItemCard = createPureComponent<Props>(
  'ItemCard',
  ({
    item,
    cardSize,
    categoriesWish,
    descriptionUnderRef,
    imageHeight,
    annimState,
    onImageLoaded,
    onLoadingDone,
    isTouch,
  }) => {
    const imgRatio = PostListItem.getOverviewImageRatio(item);
    const imgSize = imageHeight ? SizeForRatio.create(cardSize.width, imageHeight) : cardSize;
    const externalSize = SizeForRatio.resolveForRatio(imgSize, imgRatio);

    const renderCategories = (hover: boolean, ignoreHover: boolean) => (
      <CategoryHandler
        wish={categoriesWish}
        handleResolved={categories => {
          const cats = Categories.findByIds(categories, PostListItem.getCategoryIds(item));
          return (
            <CategoriesWrapper hover={hover || ignoreHover}>
              <TextOverlayCategory>
                {Categories.filterBasics(cats)
                  .map(cat => {
                    return <LocalizedText key={Category.getId(cat)} content={Category.getName(cat)} />;
                  })
                  .reduce((acc: Array<JSX.Element>, catItem, index, arr) => {
                    if (catItem !== null) {
                      acc.push(catItem);
                    }
                    if (index < arr.length - 1) {
                      acc.push(<span key={index + 'separ'}>, </span>);
                    }
                    return acc;
                  }, [])}
              </TextOverlayCategory>
              <TextOverlayClient>
                {Categories.filterClients(cats)
                  .map(cat => {
                    return <LocalizedText key={Category.getId(cat)} content={Category.getName(cat)} />;
                  })
                  .reduce((acc: Array<JSX.Element>, catItem, index, arr) => {
                    if (catItem !== null) {
                      acc.push(catItem);
                    }
                    if (index < arr.length - 1) {
                      acc.push(<span key={index + 'separ'}> / </span>);
                    }
                    return acc;
                  }, [])}
              </TextOverlayClient>
            </CategoriesWrapper>
          );
        }}
      />
    );

    return (
      <HoverProvider>
        {hParams => {
          const hover = isTouch === false && hParams.hover && annimState === 'done';
          return (
            <ItemCardWrapper innerRef={hParams.ref}>
              <NavProvider routeType={RouteType.Post} routeId={PostListItem.getId(item)} requestNavigation={true}>
                {navParams => (
                  <LinkBlock {...navParams.getAnchorProps({})}>
                    <ImageSizeWrapper pHeight={externalSize.height}>
                      <MzImageDisplay
                        annim={false}
                        zIndex={1}
                        image={PostListItem.getOverviewImage(item)}
                        size={imgSize}
                        opacity={
                          annimState === 'done' || annimState === 'requested' || annimState === 'not-requested' ? 1 : 0
                        }
                        onLoad={() => {
                          if (onImageLoaded) {
                            onImageLoaded();
                          }
                        }}
                      />
                      <Overlay hover={hover} />
                      <TextOverlay>
                        <TextOverlayTitle hover={hover}>
                          <LocalizedText content={PostListItem.getOverviewTitle(item)} />
                        </TextOverlayTitle>
                        <WaveWrapper hover={hover}>
                          <Wave width={80} color={Colors.white} centered={true} animate={true} />
                        </WaveWrapper>
                        {renderCategories(hover, false)}
                      </TextOverlay>
                      <AnnimColorPose
                        bgColor={PostListItem.getOverviewColor(item)}
                        pose={annimState}
                        realHeight={imageHeight}
                        onPoseComplete={() => {
                          if (annimState === 'loading' && onLoadingDone) {
                            onLoadingDone();
                          }
                        }}
                      />
                      <AnnimColorRequested hide={annimState !== 'not-requested'} />
                    </ImageSizeWrapper>
                    <DescriptionUnder innerRef={descriptionUnderRef} opacity={annimState === 'not-requested' ? 0 : 1}>
                      <DescriptionUnderTitle>
                        <LocalizedText content={PostListItem.getOverviewTitle(item)} />
                      </DescriptionUnderTitle>
                      {renderCategories(hover, true)}
                    </DescriptionUnder>
                  </LinkBlock>
                )}
              </NavProvider>
            </ItemCardWrapper>
          );
        }}
      </HoverProvider>
    );
  }
);



// WEBPACK FOOTER //
// ./src/module/View/ItemCard/index.tsx